import Echo from 'laravel-echo'
import Pusher from 'pusher-js'

export type MessagingMessageCreatedPayload = {
  direction: 'inbound' | 'outbound' | string
  phone_number_id: string
  message_thread_id: string
  message: Record<string, unknown>
  thread: Record<string, unknown>
}

/** Same shape as create — used when status_enum changes after SendMessageJob / carrier webhook. */
export type MessagingMessageUpdatedPayload = MessagingMessageCreatedPayload

type EchoInstance = Echo<'reverb'>

declare global {
  interface Window {
    Pusher: typeof Pusher
    Echo?: EchoInstance
  }
}

let echoInstance: EchoInstance | null = null

function reverbConfigured(): boolean {
  return Boolean(import.meta.env.VITE_REVERB_APP_KEY?.trim())
}

function authToken(): string | null {
  return localStorage.getItem('auth_token')
}

function broadcastingAuthUrl(): string {
  // Prefer same-origin proxied path in Vite; fall back to API host in production.
  const target = (import.meta.env.VITE_API_TARGET || '').replace(/\/$/, '')
  if (import.meta.env.DEV) {
    return '/broadcasting/auth'
  }
  if (target) {
    return `${target}/broadcasting/auth`
  }
  return '/broadcasting/auth'
}

/**
 * Create (or return) the shared Laravel Echo client configured for Reverb.
 * Returns null when VITE_REVERB_APP_KEY is unset so callers can fall back to polling.
 */
export function getEcho(): EchoInstance | null {
  if (!reverbConfigured()) {
    return null
  }

  if (echoInstance) {
    return echoInstance
  }

  const key = import.meta.env.VITE_REVERB_APP_KEY
  const host = import.meta.env.VITE_REVERB_HOST || window.location.hostname
  const port = Number(import.meta.env.VITE_REVERB_PORT || 8080)
  const scheme = import.meta.env.VITE_REVERB_SCHEME || 'http'
  const forceTLS = scheme === 'https'

  window.Pusher = Pusher

  echoInstance = new Echo({
    broadcaster: 'reverb',
    key,
    wsHost: host,
    wsPort: port,
    wssPort: port,
    forceTLS,
    enabledTransports: ['ws', 'wss'],
    authEndpoint: broadcastingAuthUrl(),
    auth: {
      headers: {
        Accept: 'application/json',
        ...(authToken() ? { Authorization: `Bearer ${authToken()}` } : {})
      }
    },
    authorizer: (channel) => ({
      authorize: (socketId, callback) => {
        const token = authToken()
        void fetch(broadcastingAuthUrl(), {
          method: 'POST',
          credentials: 'include',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          },
          body: JSON.stringify({
            socket_id: socketId,
            channel_name: channel.name
          })
        })
          .then(async (response) => {
            if (!response.ok) {
              const text = await response.text().catch(() => '')
              throw new Error(text || `Broadcast auth failed (${response.status})`)
            }
            return response.json()
          })
          .then((data) => callback(null, data))
          .catch((error: unknown) => {
            console.error('[Echo] channel auth failed:', error)
            callback(error instanceof Error ? error : new Error(String(error)), {
              auth: ''
            })
          })
      }
    })
  })

  window.Echo = echoInstance
  return echoInstance
}

/** True when Reverb env is present (Echo can be created). */
export function isRealtimeEnabled(): boolean {
  return reverbConfigured()
}

/** Disconnect Echo (logout / token change). */
export function disconnectEcho(): void {
  if (!echoInstance) return
  try {
    echoInstance.disconnect()
  } catch (error) {
    console.warn('[Echo] disconnect error:', error)
  }
  echoInstance = null
  if (window.Echo) {
    delete window.Echo
  }
}

/** Recreate Echo after login / token refresh so auth headers pick up the new token. */
export function resetEcho(): void {
  disconnectEcho()
  if (authToken() && reverbConfigured()) {
    getEcho()
  }
}
